import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpDataAccess } from '@softline/core';
import {
  DEFAULT_BELEGLESER_CONFIG,
  SOFTLINE_CONFIG_BELEGLESER,
} from '../belegleser.shared';
import { belegleserLocation } from '../repositories/belegleser-location';
import { FieldSchema, FieldSchemaResponse } from '../types/field-schema';
import { EMPTY_FIELD_CATALOG, FieldCatalog } from '../types/fields';
import { DocumentType } from '../types/result';

export type FieldCatalogState = 'no-type' | 'loading' | 'ready' | 'failed';

export function fieldSchemaToCatalog(schema: FieldSchema): FieldCatalog {
  return {
    groups: schema.groups,
    areas: schema.areas.map((area) => ({
      category: area.category,
      label: area.label,
      takesReferenceKind: area.takes_reference_kind ?? null,
    })),
    referenceKinds: schema.reference_kinds ?? [],
    fields: [...schema.fields]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((field) => ({
        path: field.path,
        scope: field.scope,
        group: field.group,
        label: field.label,
        required: !!field.required,
        requiredOneOf: field.required_one_of ?? null,
        multiline: !!field.multiline,
        visibility: field.visibility ?? 'primary',
        repeated: !!field.repeated,
        bboxKey: field.bbox_key ?? null,
        bboxInEntry: !!field.bbox_in_entry,
        templateCategory: field.template_category,
        takesReferenceKind: field.takes_reference_kind ?? null,
        derivedFrom: field.derived_from ?? null,
        hint: field.hint ?? null,
      })),
  };
}

/** The schema of a type out of the list; filtered by the service, so normally the only one. */
export function schemaForType(
  response: FieldSchemaResponse,
  type: DocumentType
): FieldSchema | undefined {
  return response.find((schema) => schema.document_type === type) ?? response[0];
}

/**
 * The field catalog of the open document type, as the service describes it. It is the
 * only source of fields — a catalog change reaches the UI without a frontend build.
 * Until it is loaded the catalog is empty and `state` tells why.
 */
@Injectable()
export class FieldCatalogService {
  private readonly dataAccess = inject(HttpDataAccess);
  private readonly config = inject(SOFTLINE_CONFIG_BELEGLESER);

  private readonly cache = new Map<DocumentType, FieldCatalog>();
  private readonly _documentType = signal<DocumentType | null>(null);
  private readonly _catalog = signal<FieldCatalog | null>(null);
  private readonly _state = signal<FieldCatalogState>('no-type');

  readonly state = this._state.asReadonly();
  readonly ready = computed(() => this._state() === 'ready');
  readonly catalog = computed<FieldCatalog>(() => this._catalog() ?? EMPTY_FIELD_CATALOG);

  /** Switches to the catalog of a document type; loads it once per type. */
  async use(type: DocumentType | null): Promise<void> {
    this._documentType.set(type);
    if (!type) {
      this._catalog.set(null);
      this._state.set('no-type');
      return;
    }
    const cached = this.cache.get(type);
    if (cached) {
      this._catalog.set(cached);
      this._state.set('ready');
      return;
    }
    this._catalog.set(null);
    this._state.set('loading');
    try {
      const path =
        this.config.fieldSchemaPath || DEFAULT_BELEGLESER_CONFIG.fieldSchemaPath;
      const response = await this.dataAccess.get<FieldSchemaResponse>(
        belegleserLocation(this.config, path, undefined, { document_type: type })
      );
      const schema = schemaForType(response, type);
      if (!schema) throw new Error(`no field schema for ${type}`);
      const catalog = fieldSchemaToCatalog(schema);
      this.cache.set(type, catalog);
      if (this._documentType() !== type) return;
      this._catalog.set(catalog);
      this._state.set('ready');
    } catch {
      if (this._documentType() === type) this._state.set('failed');
    }
  }

  retry(): Promise<void> {
    return this.use(this._documentType());
  }
}
